import { IStore } from 'redux/IStore';
import {
	IServicesFilterResults,
	IServicesList,
	IShopBookingTemplates,
	IShopBookingTemplatesAction,
} from 'models/shop/shopBookingTemplates';
import { Dispatch } from 'redux';
import {
	fetchBookingTemplateVariants,
	getBookingTemplateById,
	getOfficeBookingTemplates,
	getServiceGroupBookingTemplates,
} from '../helpers';
import { handleSettledPromises, requestHelper } from 'redux/helpers';
import { deleteBookingTemplate } from './helpers';
import { groupByUnique } from 'containers/Shop/Helpers';

/** Action Types */
export const GET_REQUEST: string = 'shopBookingTemplates/GET_REQUEST';
export const GET_SUCCESS: string = 'shopBookingTemplates/GET_SUCCESS';
export const MANAGE_REQUEST: string = 'shopBookingTemplates/MANAGE_REQUEST';
export const MANAGE_SUCCESS: string = 'shopBookingTemplates/MANAGE_SUCCESS';
export const REQUEST_FAILURE: string = 'shopBookingTemplates/REQUEST_FAILURE';
export const RESET_DATA: string = 'shopBookingTemplates/RESET_DATA';

/** Initial State */
const initialState: IShopBookingTemplates = {
	services: [],
	searchResults: [],
};

/** Reducer */
export function shopBookingTepmplatesReducer(
	state = initialState,
	action: IShopBookingTemplatesAction,
): IShopBookingTemplates {
	switch (action.type) {
		case MANAGE_REQUEST:
		case GET_REQUEST:
			return { ...state, isFetching: true, error: false };

		case MANAGE_SUCCESS:
		case GET_SUCCESS:
			return {
				...state,
				isFetching: false,
				services: action.payload?.services || state.services || [],
				servicesByShop: action.payload?.servicesByShop || state.servicesByShop || [],
				searchResults: action.payload?.searchResults || state.searchResults || [],
				servicesById: action.payload?.servicesById || state.servicesById,
				servicesByShopById: action.payload?.servicesByShopById || state.servicesByShopById,
				searchResultsById: action.payload?.searchResultsById || state.searchResultsById,
			};

		case REQUEST_FAILURE:
			return { ...state, isFetching: false, message: action.payload.message, error: true };

		case RESET_DATA:
			return { ...initialState };

		default:
			return state;
	}
}

/** Async Action Creator */
export function getBookingTemplatesByOffice(
	officeId: string,
	loadResources?: boolean,
	key: 'services' | 'searchResults' = 'services',
	shouldAddExisting: boolean = false,
	sortByName: boolean = false,
	groupById?: boolean,
) {
	if (loadResources) {
		console.warn('Loading booking templates from office');
	}
	return (dispatch, getState: () => IStore): Promise<any> => {
		if (officeId) {
			dispatch(getShopBookingTemplatesRequest());
			return getOfficeBookingTemplates(getState, officeId, loadResources)
				.then((templates) => {
					if (shouldAddExisting) {
						templates = [...getState().shopBookingTemplates[key], ...templates];
					}
					if (sortByName) {
						templates = templates.sort((a, b) => a?.name?.localeCompare(b?.name));
					}
					return dispatch(getShopBookingTemplatesSuccess(templates, key, groupById));
				})
				.catch((err) => dispatch(requestFailure(err)));
		} else {
			dispatch(resetBookingTemplatesData());
			return Promise.resolve(0);
		}
	};
}

export function getBookingTemplatesByServiceGroup(serviceGroupId: string, loadResources?: boolean) {
	return (dispatch, getState: () => IStore): Promise<any> => {
		if (serviceGroupId) {
			dispatch(getShopBookingTemplatesRequest());
			return getServiceGroupBookingTemplates(getState, serviceGroupId, loadResources)
				.then((templates) => {
					return dispatch(getShopBookingTemplatesSuccess(templates));
				})
				.catch((err) => dispatch(requestFailure(err)));
		} else {
			dispatch(resetBookingTemplatesData());
			return Promise.resolve(0);
		}
	};
}

/** Async Action Creator */
export function getBookingTemplatesById(
	templateId: string,
	loadResources?: boolean,
	removeTemplatesWoResources?: boolean,
	loadChildrenBookingTemplates?: boolean,
	groupById?: boolean,
) {
	return (dispatch, getState: () => IStore): Promise<any> => {
		if (templateId) {
			dispatch(getShopBookingTemplatesRequest());
			return getBookingTemplateById(getState, templateId, loadResources, loadChildrenBookingTemplates)
				.then((res) => {
					let currentTemplates =
						(getState()?.shopBookingTemplates?.services && [...getState().shopBookingTemplates.services]) || [];
					const templateExistsIndex = currentTemplates.findIndex((x) => x.id === res.id);
					if (templateExistsIndex === -1) {
						currentTemplates = [...currentTemplates, res];
					} else {
						currentTemplates[templateExistsIndex] = res;
					}
					/////////////////////////////////////////////////
					// NOTE REMOVE ONLY AFTER ADDING ALL SERVICE RESOURCES
					/////////////////////////////////////////////////
					if (removeTemplatesWoResources) {
						currentTemplates = currentTemplates.filter((x) => x.resources);
					}
					/////////////////////////////////////////////////
					return dispatch(getShopBookingTemplatesSuccess(currentTemplates, undefined, groupById));
				})
				.catch((err) => {
					dispatch(requestFailure(err));
				});
		} else {
			return Promise.resolve(dispatch(resetBookingTemplatesData()));
		}
	};
}

/**
 * For use if you do not want to save the response into Redux state
 * @param templateId bookingTemplateid
 * @returns bundle bookingTemplate with children and resources
 */
export function getBundleWithChildrenAndResources(templateId: string) {
	return (_dispatch, getState: () => IStore): Promise<any> => {
		return getBookingTemplateById(getState, templateId, true, true)
			.then((res) => {
				return res;
			})
			.catch((err) => {
				return { ok: false, err };
			});
	};
}

export function removeBookingTemplates(officeId: string, templateIds: string[]) {
	return (dispatch: Dispatch<IStore>, getState: () => IStore): Promise<any> => {
		dispatch(manageShopBookingTemplatesRequest());
		return Promise.allSettled(templateIds.map((templateId) => deleteBookingTemplate(getState, templateId))).then(
			(settled) =>
				handleSettledPromises(
					settled,
					(r) => dispatch(requestFailure(r)),
					() => dispatch(getBookingTemplatesByOffice(officeId, true)),
				),
		);
	};
}

/** Async Action Creator */
export function getBookingTemplateVariants(
	varientParentIds: string[],
	loadResources?: boolean,
	loadChildrenBookingTemplates?: boolean,
	groupById?: boolean,
) {
	return (dispatch, getState: () => IStore): Promise<any> => {
		if (varientParentIds) {
			dispatch(getShopBookingTemplatesRequest());
			const bookingTemplates: any = [];
			return Promise.all(
				varientParentIds.map((varientParentId) => {
					return fetchBookingTemplateVariants(getState, varientParentId, loadResources, loadChildrenBookingTemplates)
						.then((result) => {
							result.map((res) => {
								bookingTemplates.push(res);
							});
						})
						.catch((err) => {
							dispatch(requestFailure(err));
							throw err;
						});
				}),
			)
				.then(() => {
					dispatch(getShopBookingTemplatesSuccess(bookingTemplates, undefined, groupById));
				})
				.catch((err) => {
					dispatch(requestFailure(err));
					throw err;
				});
		} else {
			dispatch(resetBookingTemplatesData());
		}
	};
}

export function shopBookingTemplatesSearch(
	shopId: string,
	officeId: string,
	key: 'services' | 'searchResults' = 'services',
	groupById?: boolean,
) {
	//TODO handle many ShopTypes filter if needed
	return (dispatch, getState: () => IStore) => {
		if (shopId && officeId) {
			const body = {
				shops: [shopId],
				offices: [officeId],
				currentPage: 1,
				itemsPerPage: 10000,
				published: true,
				// searchKeyword: "",
				groupVariants: false,
			};

			dispatch(getShopBookingTemplatesRequest());
			return requestHelper<IServicesFilterResults>(
				fetch(
					getState().fetchDefault.baseAPI + 'api/Shop/BookingTemplates/Search/',
					Object.assign({}, getState().fetchDefault.requestInit, {
						method: 'POST',
						body: JSON.stringify(body),
					}),
				),
			)
				.then((res: IServicesFilterResults) => {
					dispatch(getShopBookingTemplatesSuccess(res.bookingTemplates.items, key, groupById));
				})
				.catch((err) => {
					dispatch(requestFailure(err));
				});
		}
	};
}

export function shopBookingTemplatesGet(
	officeId: string,
	key: 'services' | 'searchResults' = 'services',
	groupById?: boolean,
) {
	return (dispatch, getState: () => IStore) => {
		dispatch(getShopBookingTemplatesRequest());
		return getOfficeBookingTemplates(getState, officeId, true)
			.then((res) => {
				dispatch(getShopBookingTemplatesSuccess(res, key, groupById));
			})
			.catch((err) => {
				dispatch(requestFailure(err));
			});
	};
}

// /** Action Creator */
export function manageShopBookingTemplatesRequest(): IShopBookingTemplatesAction {
	return {
		type: MANAGE_REQUEST,
	};
}
export function getShopBookingTemplatesRequest(
	key: 'servicesByShop' | 'services' | 'searchResults' = 'services',
): IShopBookingTemplatesAction {
	return {
		type: GET_REQUEST,
		payload: {
			[key]: [],
		},
	};
}

// /** Action Creator */
export function manageShopBookingTemplatesSuccess(): IShopBookingTemplatesAction {
	return {
		type: MANAGE_SUCCESS,
	};
}
export function getShopBookingTemplatesSuccess(
	services: IServicesList[],
	key: 'servicesByShop' | 'services' | 'searchResults' = 'services',
	groupById?: boolean,
): IShopBookingTemplatesAction {
	const payload: IShopBookingTemplatesAction['payload'] = {
		[key]: services,
	};

	if (groupById) {
		payload[key + 'ById'] = groupByUnique(services || [], 'id');
	}

	return {
		type: GET_SUCCESS,
		payload,
	};
}

export function requestFailure(message: any): IShopBookingTemplatesAction {
	return {
		type: REQUEST_FAILURE,
		payload: {
			message,
		},
	};
}

// /** Action Creator */
export function resetBookingTemplatesData(): IShopBookingTemplatesAction {
	return {
		type: RESET_DATA,
	};
}
